import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/Header";


export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — Real Pranks" },
      {
        name: "description",
        content:
          "The terms that govern your use of the Real Pranks website and services.",
      },
      { property: "og:title", content: "Terms of Service — Real Pranks" },
      {
        property: "og:description",
        content: "The rules of using Real Pranks.",
      },
    ],
  }),
  component: TermsPage,
});

const LAST_UPDATED = "April 22, 2026";

function TermsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-12 md:py-16">
        <span className="text-[10px] font-bold tracking-[0.25em] text-muted-foreground uppercase">
          Legal
        </span>
        <h1 className="mt-2 text-3xl font-bold md:text-4xl">Terms of service</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Last updated: {LAST_UPDATED}
        </p>

        <div className="mt-10 space-y-6 text-sm leading-relaxed text-foreground/90">
          <Section title="1. Acceptance of terms">
            <p>
              By accessing or using the Real Pranks website and services (the
              “Service”), you agree to be bound by these Terms of Service
              (“Terms”). If you do not agree, do not use the Service.
            </p>
          </Section>

          <Section title="2. Eligibility">
            <p>
              You must be at least 13 years old to use the Service. If you are
              under 18, you must have permission from a parent or legal
              guardian.
            </p>
          </Section>

          <Section title="3. Accounts">
            <p>
              You are responsible for keeping your password secure and for all
              activity on your account. Notify us immediately at
              members@rosscreations.com if you suspect unauthorized access.
            </p>
          </Section>

          <Section title="4. Subscriptions and billing">
            <ul className="ml-6 list-disc space-y-1">
              <li>
                Subscriptions renew automatically at the end of each billing
                period until canceled.
              </li>
              <li>
                You can cancel anytime from your account page; cancellation
                takes effect at the end of the current billing period.
              </li>
              <li>
                Fees are non-refundable except where required by law or at our
                sole discretion.
              </li>
            </ul>
          </Section>

          <Section title="5. Content and license">
            <p>
              All content on the Service — including videos, images, text, and
              merchandise designs — is owned by RossCreations or its licensors
              and is protected by copyright and other intellectual property
              laws. We grant you a limited, personal, non-transferable,
              non-exclusive license to access and view the content for your
              own personal, non-commercial use while your subscription is
              active.
            </p>
          </Section>

          <Section title="6. Prohibited conduct">
            <ul className="ml-6 list-disc space-y-1">
              <li>
                Do not download, copy, redistribute, or rebroadcast any content
                from the Service.
              </li>
              <li>
                Do not share your account credentials or attempt to circumvent
                access controls.
              </li>
              <li>
                Do not use the Service to harass, defame, or harm others.
              </li>
              <li>
                Do not attempt to disrupt, reverse engineer, or scrape the
                Service.
              </li>
            </ul>
          </Section>

          <Section title="7. Termination">
            <p>
              We may suspend or terminate your access to the Service at any
              time for any reason, including violation of these Terms. You may
              stop using the Service at any time by canceling your
              subscription and deleting your account.
            </p>
          </Section>

          <Section title="8. Disclaimers">
            <p>
              The Service is provided “as is” and “as available,” without
              warranties of any kind, whether express or implied. We do not
              warrant that the Service will be uninterrupted, error-free, or
              free of harmful components.
            </p>
          </Section>

          <Section title="9. Limitation of liability">
            <p>
              To the maximum extent permitted by law, RossCreations and its
              affiliates will not be liable for any indirect, incidental,
              special, consequential, or punitive damages, or any loss of
              profits or revenues, whether incurred directly or indirectly.
            </p>
          </Section>

          <Section title="10. Governing law">
            <p>
              These Terms are governed by the laws of the State of Florida,
              United States, without regard to its conflict of law principles.
              Any dispute arising from these Terms or the Service will be
              resolved exclusively in the state or federal courts located in
              Florida.
            </p>
          </Section>

          <Section title="11. Changes to these terms">
            <p>
              We may update these Terms from time to time. When we do, we will
              revise the “Last updated” date above and, when the changes are
              material, notify you by email or in the Service.
            </p>
          </Section>

          <Section title="12. Contact us">
            <p>
              Questions about these Terms? Email{" "}
              <a
                href="mailto:members@rosscreations.com"
                className="underline underline-offset-4"
              >
                members@rosscreations.com
              </a>
              .
            </p>
          </Section>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-lg font-bold">{title}</h2>
      <div className="mt-2 space-y-2">{children}</div>
    </section>
  );
}
